import { Box, Flex, Text } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";
import { getBillingCountryHintUrl, getPriceEstimateUrl } from "../../utils/billingUrls";
import { PRICE_STYLE } from "../dashboard/tokens";

type PriceEstimate = {
    source: "stripe_tax";
    country: string;
    currency: string;
    baseAmount: number;
    taxAmount: number;
    totalAmount: number;
    taxBehavior: "exclusive" | "inclusive";
    estimated: true;
    notice: string;
};

const COUNTRIES: {
    code: string;
    label: string;
}[] = [
    {
        code: "AT",
        label: "Austria",
    },
    {
        code: "BE",
        label: "Belgium",
    },
    {
        code: "BG",
        label: "Bulgaria",
    },
    {
        code: "HR",
        label: "Croatia",
    },
    {
        code: "CY",
        label: "Cyprus",
    },
    {
        code: "CZ",
        label: "Czechia",
    },
    {
        code: "DK",
        label: "Denmark",
    },
    {
        code: "EE",
        label: "Estonia",
    },
    {
        code: "FI",
        label: "Finland",
    },
    {
        code: "FR",
        label: "France",
    },
    {
        code: "DE",
        label: "Germany",
    },
    {
        code: "GR",
        label: "Greece",
    },
    {
        code: "HU",
        label: "Hungary",
    },
    {
        code: "IE",
        label: "Ireland",
    },
    {
        code: "IT",
        label: "Italy",
    },
    {
        code: "LV",
        label: "Latvia",
    },
    {
        code: "LT",
        label: "Lithuania",
    },
    {
        code: "LU",
        label: "Luxembourg",
    },
    {
        code: "MT",
        label: "Malta",
    },
    {
        code: "NL",
        label: "Netherlands",
    },
    {
        code: "PL",
        label: "Poland",
    },
    {
        code: "PT",
        label: "Portugal",
    },
    {
        code: "RO",
        label: "Romania",
    },
    {
        code: "SK",
        label: "Slovakia",
    },
    {
        code: "SI",
        label: "Slovenia",
    },
    {
        code: "ES",
        label: "Spain",
    },
    {
        code: "SE",
        label: "Sweden",
    },
    {
        code: "GB",
        label: "United Kingdom",
    },
    {
        code: "US",
        label: "United States",
    },
    {
        code: "OTHER",
        label: "Other countries",
    },
];

const KNOWN_COUNTRIES = new Set(COUNTRIES.map((country) => country.code));
const COUNTRY_STORAGE_KEY = "dc_pro_pricing_country";
type CountrySource = "manual" | "ip" | "unknown";

function savedCountry(): string {
    try {
        const value = window.localStorage.getItem(COUNTRY_STORAGE_KEY);
        return value && KNOWN_COUNTRIES.has(value) ? value : "";
    } catch {
        return "";
    }
}

function saveCountry(value: string) {
    try {
        if (value) {
            window.localStorage.setItem(COUNTRY_STORAGE_KEY, value);
        } else {
            window.localStorage.removeItem(COUNTRY_STORAGE_KEY);
        }
    } catch {
        // Pricing still works when browser storage is unavailable.
    }
}

function formatAmount(cents: number, currency: string): string {
    return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
    }).format(cents / 100);
}

const fieldStyle = {
    color: "var(--dc-text, var(--gray-12))",
    background: "var(--dc-panel-bg)",
    border: "1px solid var(--gray-a7)",
    borderRadius: 6,
    padding: "7px 9px",
    maxWidth: "100%",
};

export default function ProPricePreview() {
    const [country, setCountry] = useState(() => savedCountry() || "OTHER");
    const [countrySource, setCountrySource] = useState<CountrySource>(() => (savedCountry() ? "manual" : "unknown"));
    const [countryDetecting, setCountryDetecting] = useState(() => !savedCountry());
    const userChangedCountry = useRef(false);
    const [estimate, setEstimate] = useState<PriceEstimate | null>(null);
    const [status, setStatus] = useState<"idle" | "loading" | "ready" | "unavailable">("idle");
    const countryEstimate = estimate?.country === country ? estimate : null;
    const needsMoreLocation = country === "US";
    const isOtherCountry = country === "OTHER";

    useEffect(() => {
        if (savedCountry()) {
            return;
        }
        const controller = new AbortController();
        const fallBackCountry = () => {
            if (controller.signal.aborted || userChangedCountry.current) {
                return;
            }
            setCountry("OTHER");
            setCountrySource("unknown");
        };
        void fetch(getBillingCountryHintUrl(), {
            credentials: "omit",
            signal: controller.signal,
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error("Geo hint unavailable");
                }
                return (await response.json()) as {
                    country?: string | null;
                };
            })
            .then((data) => {
                if (controller.signal.aborted || userChangedCountry.current) {
                    return;
                }
                const detected = typeof data.country === "string" ? data.country.toUpperCase() : "";
                if (detected && KNOWN_COUNTRIES.has(detected)) {
                    setCountry(detected);
                    setCountrySource("ip");
                } else {
                    fallBackCountry();
                }
            })
            .catch(fallBackCountry)
            .finally(() => {
                if (!controller.signal.aborted) {
                    setCountryDetecting(false);
                }
            });
        return () => controller.abort();
    }, []);

    useEffect(() => {
        setEstimate(null);
        if (needsMoreLocation || isOtherCountry) {
            setStatus("idle");
            return;
        }
        const controller = new AbortController();
        setStatus("loading");
        const timeout = window.setTimeout(() => {
            void fetch(getPriceEstimateUrl(country), {
                credentials: "omit",
                signal: controller.signal,
            })
                .then(async (response) => {
                    if (!response.ok) {
                        throw new Error("Estimate unavailable");
                    }
                    return (await response.json()) as PriceEstimate;
                })
                .then((result) => {
                    if (!controller.signal.aborted) {
                        setEstimate(result);
                        setStatus("ready");
                    }
                })
                .catch(() => {
                    if (!controller.signal.aborted) {
                        setStatus("unavailable");
                    }
                });
        }, 0);
        return () => {
            window.clearTimeout(timeout);
            controller.abort();
        };
    }, [
        country,
        needsMoreLocation,
        isOtherCountry,
    ]);

    return (
        <Box>
            <Flex align="baseline" gap="2" wrap="wrap">
                <Text size="8" style={PRICE_STYLE} data-testid="pro-estimate-price">
                    {countryEstimate ? formatAmount(countryEstimate.totalAmount, countryEstimate.currency) : "$20 + taxes"}
                </Text>
                <Text
                    size="2"
                    style={{
                        color: "var(--dc-muted-2)",
                    }}
                >
                    / month
                </Text>
            </Flex>
            <Flex direction="column" gap="2" mt="3">
                <label
                    htmlFor="pro-billing-country"
                    style={{
                        fontSize: 12,
                        color: "var(--dc-muted-2)",
                    }}
                >
                    Your billing country
                </label>
                <select
                    id="pro-billing-country"
                    value={country}
                    onChange={(event) => {
                        const selected = event.target.value;
                        userChangedCountry.current = true;
                        setCountry(selected);
                        setCountrySource("manual");
                        setCountryDetecting(false);
                        saveCountry(selected);
                    }}
                    style={fieldStyle}
                >
                    {COUNTRIES.map((option) => (
                        <option key={option.code} value={option.code}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <Text size="1" color="gray">
                    {countrySource === "ip"
                        ? "Country suggested from your IP address; choose another if your billing address differs."
                        : countrySource === "manual"
                          ? "Using your chosen billing country."
                          : countryDetecting
                            ? "Detecting your country…"
                            : "Choose your billing country if it is listed."}
                </Text>
            </Flex>
            <Text as="p" size="2" color="gray" mt="3" data-testid="pro-estimate-note">
                {isOtherCountry
                    ? "Taxes depend on your billing address. The final total is shown at checkout."
                    : countryEstimate
                      ? `Estimated total including ${formatAmount(countryEstimate.taxAmount, countryEstimate.currency)} calculated tax.`
                      : status === "loading"
                        ? "Calculating an estimated total with Stripe Tax…"
                        : status === "unavailable"
                          ? "Tax estimate unavailable. Taxes are calculated at checkout."
                          : needsMoreLocation
                            ? "US sales tax depends on your billing address. The final total is shown at checkout."
                            : "Calculating estimated taxes…"}
            </Text>
            <Text as="p" size="1" color="gray" mt="2">
                {countryEstimate
                    ? "Tax-inclusive Stripe estimate in USD for the selected country. Final checkout tax can differ after your full billing details, VAT ID and discounts."
                    : "Final checkout tax is calculated using your full billing details, VAT ID and discounts."}
            </Text>
        </Box>
    );
}
